import React from 'react';
import { Table, Badge, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  FaEye,
  FaTrash,
  FaCheckCircle,
  FaBook,
  FaThumbsUp,
  FaFileExport,
  FaFileAlt,
  FaSync,
  FaCalculator,
  FaRobot,
} from 'react-icons/fa';
import { BsCheckCircleFill, BsFillXCircleFill, BsClockFill } from 'react-icons/bs';

/**
 * Componente para mostrar la tabla de planillas
 */
const PlanillasTable = ({
  planillas,
  onProcesar,
  onAprobar,
  onEliminar,
  onAuditar,
  operacionLoading
}) => {
  const navigate = useNavigate();

  const formatearFecha = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(
      timestamp.seconds ? timestamp.seconds * 1000 : timestamp
    );
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getBadgeVariant = (estado) => {
    switch (estado.toUpperCase()) {
      case "BORRADOR":
        return "warning";
      case "PROCESADO":
        return "info";
      case "APROBADO":
        return "success";
      case "ANULADO":
        return "danger";
      default:
        return "secondary";
    }
  };

  const handleVerBoletas = (planillaId) => {
    navigate(`/proceso-planilla/${planillaId}`);
  };

  const handleExportarArchivos = (planillaId) => {
    navigate(`/planillas/${planillaId}/exportacion`);
  };

  const handleVerAsientos = (planillaId) => {
    navigate(`/planillas/${planillaId}/asientos`);
  };

  if (planillas.length === 0) {
    return (
      <div className="text-center py-5">
        <FaBook size={48} className="text-muted mb-3" />
        <h5>No hay planillas disponibles</h5>
        <p className="text-muted">Crea tu primera planilla para comenzar</p>
      </div>
    );
  }

  return (
    <Table responsive hover className="table-planillas">
      <thead className="table-dark">
        <tr>
          <th>Período</th>
          <th>Fecha Inicio</th>
          <th>Fecha Fin</th>
          <th>Estado</th>
          <th>Empleados</th>
          <th>Total Neto</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {planillas.map((planilla) => (
          <tr key={planilla.id}>
            <td className="fw-bold">{planilla.periodo}</td>
            <td>{formatearFecha(planilla.fecha_inicio)}</td>
            <td>{formatearFecha(planilla.fecha_fin)}</td>
            <td>
              <Badge bg={getBadgeVariant(planilla.estado)}>
                {planilla.estado}
              </Badge>
            </td>
            <td className="text-center">{planilla.total_empleados || 0}</td>
            <td className="text-end fw-bold">
              S/ {planilla.total_neto?.toFixed(2) || '0.00'}
            </td>
            <td>
              <div className="d-flex gap-1 flex-wrap">
                {/* Ver Detalle Planilla */}
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Ver Detalle Planilla</Tooltip>}
                >
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleVerBoletas(planilla.id)}
                  >
                    <FaEye />
                  </Button>
                </OverlayTrigger>

                {/* Boletas de Pago (PDF / ZIP) */}
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Boletas de Pago (PDF / ZIP)</Tooltip>}
                >
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => navigate(`/boletas-pago?planillaId=${planilla.id}`)}
                  >
                    <FaFileAlt />
                  </Button>
                </OverlayTrigger>

                {/* Procesar */}
                {planilla.estado === "BORRADOR" && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Procesar Planilla</Tooltip>}
                  >
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => onProcesar(planilla.id)}
                      disabled={operacionLoading}
                    >
                      {operacionLoading ? <FaSync className="fa-spin" /> : <FaCheckCircle />}
                    </Button>
                  </OverlayTrigger>
                )}

                {/* Ver Asientos Contables */}
                {(planilla.estado === "PROCESADO" || planilla.estado === "APROBADO") && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Ver Asientos Contables</Tooltip>}
                  >
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleVerAsientos(planilla.id)}
                    >
                      <FaCalculator />
                    </Button>
                  </OverlayTrigger>
                )}



                {/* Auditar (Cali AI) */}
                {planilla.estado === "PROCESADO" && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Auditar con Cali AI</Tooltip>}
                  >
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => onAuditar(planilla.id)}
                      disabled={operacionLoading}
                    >
                      <FaRobot />
                    </Button>
                  </OverlayTrigger>
                )}

                {/* Aprobar */}
                {planilla.estado === "PROCESADO" && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Aprobar Planilla</Tooltip>}
                  >
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => onAprobar(planilla.id)}
                      disabled={operacionLoading}
                    >
                      {operacionLoading ? <FaSync className="fa-spin" /> : <FaThumbsUp />}
                    </Button>
                  </OverlayTrigger>
                )}

                {/* Exportar Archivos (PLAME y AFP NET) */}
                {planilla.estado === "APROBADO" && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Exportar Archivos (PLAME / AFP)</Tooltip>}
                  >
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleExportarArchivos(planilla.id)}
                    >
                      <FaFileExport />
                    </Button>
                  </OverlayTrigger>
                )}

                {/* Eliminar */}
                {planilla.estado !== "APROBADO" && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Eliminar Planilla</Tooltip>}
                  >
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onEliminar(planilla.id, planilla.estado)}
                      disabled={operacionLoading}
                    >
                      <FaTrash />
                    </Button>
                  </OverlayTrigger>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default PlanillasTable;